<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { IconFilter, IconGrid, IconList } from '@/components/design/icons';
import type { ReviewInDb } from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBoardColumnsMenu, {
  type GaiaBoardColumnsMenuItem,
} from '@/components/design/review/GaiaBoardColumnsMenu.vue';
import GaiaFilterPanel from '@/components/design/composite/GaiaFilterPanel.vue';
import GaiaFileDropper from '@/components/file-dropper/GaiaFileDropper.vue';
import { startReviewAction } from '@/components/file-dropper/actions/startReviewAction';
import StartAnalysisModal from '@/components/StartAnalysisModal.vue';
import ReviewDetailsDrawer from '@/components/review/ReviewDetailsDrawer.vue';
import ReviewsBoard from '@/components/review/ReviewsBoard.vue';
import ReviewsTable from '@/components/review/ReviewsTable.vue';
import { gaiaContentWidth, gaiaFonts } from '@/components/design/themes/tokens';
import {
  buildColumnFilters,
  useReviewFilterLabels,
  type ReviewFilterOptions,
} from '@/composables/review/useReviewSearchFilters';
import { useListQuerySync, type SortDirection } from '@/composables/useListQuerySync';
import { useReviewNavigator } from '@/composables/useReviewNavigator';
import { useKanbanStore } from '@/stores/kanbanStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useReviewStore } from '@/stores/reviewStore';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * The reviews list — one route, two shapes. Board and table were separate routes
 * and separate view files that duplicated this whole header, and the sidebar
 * carried a row for each. `?view=board|table` replaces both.
 */

const kanbanStore = useKanbanStore();
const organizationStore = useOrganizationStore();
const reviewStore = useReviewStore();
const message = useGaiaToast();
const navigateToReview = useReviewNavigator();

const {
  currentPage,
  pageSize,
  searchQuery,
  sortKey,
  sortDirection,
  sortActivated,
  activeTab: viewMode,
  columnFilters,
  setColumnFilters,
} = useListQuerySync({
  tabParam: 'view',
  defaultTab: 'board',
  allowedTabs: ['board', 'table'],
  defaultSortKey: 'name',
  defaultSortDirection: 'asc',
  allowedPageSizes: [5, 10, 25],
});

const isBoard = computed(() => viewMode.value === 'board');

// Typed by what each half exposes rather than `InstanceType`, which resolves to
// `any` for a `<script setup>` component and would silently accept anything.
const boardRef = ref<{ refresh: () => Promise<void> } | null>(null);
const tableRef = ref<{ refresh: () => Promise<void> } | null>(null);

const showFilterPanel = ref<boolean>(false);
const showNewReviewModal = ref<boolean>(false);
const stats = ref<{ total: number; running: number }>({ total: 0, running: 0 });
const columnCounts = ref<Record<string, number>>({});
const filterOptions = ref<ReviewFilterOptions>({});

const detailsReview = ref<ReviewInDb | null>(null);
const showDetailsDrawer = ref<boolean>(false);
const pendingDelete = ref<ReviewInDb | null>(null);
const pendingColumnDelete = ref<{ key: string; label: string } | null>(null);

// ── View mode ───────────────────────────────────────────────────────────────

const VIEW_OPTIONS: GaiaSegmentedOption[] = [
  { label: 'Board', value: 'board', icon: IconGrid },
  { label: 'Table', value: 'table', icon: IconList },
];

/** The table's sort, kept while the board is shown so switching back restores it. */
const stashedSort = ref<{ key: string; direction: SortDirection } | null>(null);

/**
 * The board carries neither `page` nor `sort`: it pages per column, and it has
 * to sort by `kanban_column_sorting_position` or the order it shows is one the
 * next drop contradicts. Every write here is synchronous, so `useListQuerySync`'s
 * single watcher fires once and the mode change costs one `router.replace`.
 */
function setViewMode(next: string): void {
  if (next === viewMode.value) return;

  if (next === 'board') {
    if (sortActivated.value) {
      stashedSort.value = { key: sortKey.value, direction: sortDirection.value };
    }
    currentPage.value = 1;
    sortActivated.value = false;
  } else {
    // Never restore a page number across modes — the filters may have moved
    // under it and page 3 of a different set is meaningless.
    currentPage.value = 1;
    if (stashedSort.value) {
      sortKey.value = stashedSort.value.key;
      sortDirection.value = stashedSort.value.direction;
      sortActivated.value = true;
    }
  }

  viewMode.value = next;
}

// A `?view=board&page=3&sort=name` deep link describes a state the board cannot
// hold; drop those params on entry rather than carrying them dead.
if (isBoard.value) {
  currentPage.value = 1;
  sortActivated.value = false;
}

// ── Filters ─────────────────────────────────────────────────────────────────

// `kanban_state_uid` is offered in table mode only: on the board it narrows
// which columns render rather than which rows match, and the board injects each
// column's own state into its query.
const { filterConfig, valueLabelMap } = useReviewFilterLabels();

/** What both halves send as `column_filters`. */
const apiColumnFilters = computed(() => buildColumnFilters(columnFilters.value));

watch(columnFilters, () => {
  currentPage.value = 1;
});

// ── Board columns menu ──────────────────────────────────────────────────────

const columnsMenuItems = computed<GaiaBoardColumnsMenuItem[]>(() =>
  kanbanStore.sortedKanbanStates
    .filter(state => !state.archive_state)
    .map(state => ({
      key: state.uid,
      label: state.name,
      count: columnCounts.value[state.uid] ?? 0,
      visible: !state.hidden,
      // A protected column is built in, and a column with cards in it would
      // orphan them.
      canDelete: !state.protected && (columnCounts.value[state.uid] ?? 0) === 0,
    }))
);

const archivedColumns = computed(() =>
  kanbanStore.archivedColumns.map(state => ({ key: state.uid, label: state.name }))
);

async function toggleColumn(uid: string): Promise<void> {
  try {
    await kanbanStore.toggleKanbanStateVisibility(uid);
  } catch (error) {
    logger.error('Failed to toggle column visibility:', error);
    message.error('Failed to update column');
  }
}

async function setColumnArchived(uid: string, archived: boolean): Promise<void> {
  try {
    await kanbanStore.updateKanbanState(uid, { archive_state: archived });
    message.success(archived ? 'Column archived' : 'Column restored');
  } catch (error) {
    logger.error('Failed to update column archive state:', error);
    message.error('Failed to update column');
  }
}

async function confirmColumnDelete(): Promise<void> {
  const column = pendingColumnDelete.value;
  if (!column) return;
  try {
    await kanbanStore.deleteKanbanState(column.key);
    message.success('Column deleted');
  } catch (error) {
    logger.error('Failed to delete column:', error);
    message.error('Failed to delete column');
  } finally {
    pendingColumnDelete.value = null;
  }
}

async function addColumn(name: string): Promise<void> {
  try {
    await kanbanStore.createKanbanState(name, kanbanStore.kanbanStates.length + 1);
    message.success('Column added');
  } catch (error) {
    logger.error('Failed to add column:', error);
    message.error('Failed to add column');
  }
}

// ── Rows ────────────────────────────────────────────────────────────────────

function openReview(review: ReviewInDb): void {
  void navigateToReview(review.uid);
}

function openDetails(review: ReviewInDb): void {
  detailsReview.value = review;
  showDetailsDrawer.value = true;
}

function refreshActiveView(): void {
  if (isBoard.value) void boardRef.value?.refresh();
  else void tableRef.value?.refresh();
}

async function confirmDelete(): Promise<void> {
  const review = pendingDelete.value;
  if (!review) return;
  try {
    const success = await reviewStore.deleteReview(review.uid);
    if (success) {
      message.success('Review deleted');
      refreshActiveView();
    }
  } catch (error) {
    logger.error('Failed to delete review:', error);
    message.error('Failed to delete review');
  } finally {
    pendingDelete.value = null;
  }
}

function handleSort(payload: { key: string; direction: SortDirection; activated: boolean }): void {
  sortKey.value = payload.key;
  sortDirection.value = payload.direction;
  sortActivated.value = payload.activated;
  currentPage.value = 1;
}

/** The mock's line under the title: how many, and how many still extracting. */
const countLabel = computed(() => {
  const reviews = `${stats.value.total} ${stats.value.total === 1 ? 'review' : 'reviews'}`;
  return stats.value.running > 0
    ? `${reviews} · ${stats.value.running} running in AI review`
    : reviews;
});

/** Mono "visible/total" beside the Columns button, as the mock shows it. */
const visibleColumnSummary = computed(() => {
  const all = kanbanStore.sortedKanbanStates.filter(state => !state.archive_state);
  return `${all.filter(state => !state.hidden).length}/${all.length}`;
});

const monoFont = gaiaFonts.mono;

/**
 * The board's columns scroll internally, so the row needs a definite height —
 * without one, `max-height: 100%` on a column resolves against `auto` and the
 * page scrolls instead of the cards. Same device as the playbook run grid.
 */
const boardHeightStyle = computed(() => ({ height: 'calc(100vh - 260px)' }));

onMounted(async () => {
  try {
    await Promise.all([
      kanbanStore.fetchKanbanStates(),
      organizationStore.fetchOrganizationUsers(),
    ]);
  } catch (error) {
    logger.error('Error initializing reviews:', error);
    message.error('Failed to load initial data');
  }
});
</script>

<template>
  <gaia-file-dropper :actions="[startReviewAction]">
    <!-- One width for both modes: the table cap is fixed, so switching between
         board and table never moves the header. -->
    <layout :content-max-width="gaiaContentWidth.table">
      <template #breadcrumb>
        <n-breadcrumb-item>{{ navLabels.review }}</n-breadcrumb-item>
        <n-breadcrumb-item>{{ navLabels.reviews }}</n-breadcrumb-item>
      </template>
      <n-flex vertical :size="20" :wrap="false">
        <!-- Title and the page's own actions. The count line is the title's
             subtitle rather than its own strip — one block, two lines. -->
        <n-flex align="flex-start" :size="24">
          <n-flex vertical :size="5" :wrap="false" :style="{ flex: '1 1 240px', minWidth: 0 }">
            <gaia-header :level="1" size="screen">Contract Reviews</gaia-header>
            <gaia-text size="meta" tone="faint">{{ countLabel }}</gaia-text>
          </n-flex>

          <n-flex
            align="center"
            justify="end"
            :size="10"
            :style="{ marginLeft: 'auto', flex: 'none' }"
          >
            <gaia-button @click="showFilterPanel = !showFilterPanel">
              <template #icon><n-icon :component="IconFilter" /></template>
              Filter
            </gaia-button>
            <gaia-create-button subject="review" @click="showNewReviewModal = true" />
          </n-flex>
        </n-flex>

        <!-- How the list is shown and what it is narrowed to: view switcher and
             search on the left, the board's column control on the right. -->
        <n-flex align="center" :size="12" :wrap="false">
          <gaia-segmented :value="viewMode" :options="VIEW_OPTIONS" @update:value="setViewMode" />
          <!-- Filters the loaded rows in place, so no @search hook — the board
               and table below read `searchQuery` directly. -->
          <gaia-search-input v-model:value="searchQuery" placeholder="Search reviews" />
          <!-- The margin rides on a wrapper, not on GaiaPopover: the popover
               binds its own props and lets attrs fall through to the floating
               content, so a style set on it never reaches the flex item here. -->
          <div v-if="isBoard" :style="{ marginLeft: 'auto', flex: 'none' }">
            <gaia-popover placement="bottom-end">
              <template #trigger>
                <gaia-button size="small">
                  <template #icon><n-icon :component="IconGrid" /></template>
                  Columns
                  <gaia-text size="meta" tone="faint" :style="{ fontFamily: monoFont }">
                    {{ visibleColumnSummary }}
                  </gaia-text>
                </gaia-button>
              </template>
              <gaia-board-columns-menu
                :items="columnsMenuItems"
                :archived="archivedColumns"
                @toggle="toggleColumn"
                @archive="uid => setColumnArchived(uid, true)"
                @restore="uid => setColumnArchived(uid, false)"
                @delete="
                  uid =>
                    (pendingColumnDelete = {
                      key: uid,
                      label:
                        columnsMenuItems.find(item => item.key === uid)?.label ?? 'this column',
                    })
                "
                @add="addColumn"
              />
            </gaia-popover>
          </div>
        </n-flex>

        <gaia-filter-panel
          v-model:visible="showFilterPanel"
          :filter-config="filterConfig"
          :filter-options="filterOptions"
          :active-filters="columnFilters"
          :value-label-map="valueLabelMap"
          @update:filters="setColumnFilters"
        />

        <div v-if="isBoard" :style="boardHeightStyle">
          <reviews-board
            ref="boardRef"
            :search="searchQuery"
            :column-filters="apiColumnFilters"
            :page-size="pageSize"
            @open="openReview"
            @details="openDetails"
            @delete="review => (pendingDelete = review)"
            @counts="counts => (columnCounts = counts)"
            @stats="value => (stats = value)"
            @filter-options="options => (filterOptions = options)"
          />
        </div>

        <reviews-table
          v-else
          ref="tableRef"
          :search="searchQuery"
          :column-filters="apiColumnFilters"
          :page="currentPage"
          :page-size="pageSize"
          :sort-key="sortKey"
          :sort-direction="sortDirection"
          :sort-activated="sortActivated"
          @update:page="page => (currentPage = page)"
          @update:page-size="size => (pageSize = size)"
          @sort="handleSort"
          @open="openReview"
          @details="openDetails"
          @delete="review => (pendingDelete = review)"
          @stats="value => (stats = value)"
          @filter-options="options => (filterOptions = options)"
        />
      </n-flex>
    </layout>

    <review-details-drawer
      v-model:show="showDetailsDrawer"
      :review="detailsReview"
      @saved="refreshActiveView"
    />

    <start-analysis-modal v-model:show="showNewReviewModal" @analysis-started="refreshActiveView" />

    <gaia-modal
      :show="pendingDelete !== null"
      title="Delete review"
      @update:show="value => !value && (pendingDelete = null)"
    >
      <gaia-text>
        Delete “{{ pendingDelete?.name }}”? This also removes its extractions, and cannot be undone.
      </gaia-text>
      <template #action>
        <gaia-button @click="pendingDelete = null">Cancel</gaia-button>
        <gaia-button type="error" @click="confirmDelete">Delete</gaia-button>
      </template>
    </gaia-modal>

    <gaia-modal
      :show="pendingColumnDelete !== null"
      title="Delete column"
      @update:show="value => !value && (pendingColumnDelete = null)"
    >
      <gaia-text>
        Delete the “{{ pendingColumnDelete?.label }}” column? Only empty columns can be deleted.
      </gaia-text>
      <template #action>
        <gaia-button @click="pendingColumnDelete = null">Cancel</gaia-button>
        <gaia-button type="error" @click="confirmColumnDelete">Delete</gaia-button>
      </template>
    </gaia-modal>
  </gaia-file-dropper>
</template>
